import {
  TaskOutcomeDataSchema,
  TaskOutcomeContractSchema,
  TaskOutcomeContractResponseSchema,
} from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  TASK_ASSIGNEE_TYPES,
  TASK_PROCEDURE_PHASES,
  TASK_SNOOZE_PRESETS,
  TASK_SNOOZE_REASONS,
  TASK_STATUSES,
  TERMINAL_TASK_STATUSES,
  MAX_TASK_ANCHOR_FIELDS,
  SystemEntity,
  TaskProcedureBindingSchema,
  TaskProcedureBindingsSchema,
} from "@erp/shared-types";

// --- Enums ---

// Built from the canonical status list in @erp/shared-types so the runtime
// validator and the transition state machine (in @erp/shared-utils) can
// never drift apart.
export const TaskStatusSchema = z.enum(TASK_STATUSES);

export const TaskPrioritySchema = z.enum(["low", "medium", "high", "urgent"]);

export const TaskAssigneeTypeSchema = z.enum(TASK_ASSIGNEE_TYPES);

// --- Response schema ---

export const TaskResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  // The namespace the task belongs to, its per-namespace number, and the
  // derived human-readable id (e.g. "TASK-42"). Nullable for tasks not yet
  // numbered (pre-backfill).
  taskNamespaceId: z.uuid().nullable(),
  taskNumber: z.number().int().nullable(),
  displayId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  status: TaskStatusSchema,
  priority: TaskPrioritySchema,
  assigneeType: TaskAssigneeTypeSchema.nullable(),
  assigneeUserId: z.uuid().nullable(),
  assigneeAgentDefinitionId: z.uuid().nullable(),
  createdByUserId: z.uuid().nullable(),
  dueDate: z.coerce.date().nullable(),
  // Current shared snooze. Null, or a time in the past, means visible.
  snoozedUntil: z.coerce.date().nullable().optional(),
  snoozedByUserId: z.uuid().nullable().optional(),
  snoozeClearsOnEdit: z.boolean().optional(),
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  tags: z.array(z.string()).nullable(),
  // Optional link into the org's task type registry (null = plain task).
  taskTypeId: z.uuid().nullable(),
  // Self-link making this task a subtask of another task.
  parentTaskId: z.uuid().nullable(),
  // The business entity a typed task is anchored to (both set or both null).
  anchorEntityType: z.enum(SystemEntity).nullable(),
  anchorEntityId: z.uuid().nullable(),
  // Key of the outside source record a synced task stands for (e.g. a Famous
  // SO number). Optional so older serializers stay valid.
  sourceKey: z.string().nullable().optional(),
  // Terminal result, set only when the task enters completed/failed/cancelled.
  outcome: z.string().nullable(),
  outcomeReason: z.string().nullable(),
  outcomeData: TaskOutcomeDataSchema.nullable().optional(),
  outcomeContractSnapshot: TaskOutcomeContractSchema.nullable().optional(),
  // Per-task playbook overrides (raw). The merged, hydrated playbook is
  // served by `getPlaybook`. Optional so older serializers stay valid.
  procedures: z.array(TaskProcedureBindingSchema).nullable().optional(),
  // Validated create-time payload matching the type's input schema.
  input: z.record(z.string(), z.unknown()).nullable().optional(),
  // Whole-object replacement of `input`. Null means no edits yet. Whole-record
  // readers, including submit, use `correction ?? input` and do not fill
  // missing keys from input. Playbook `$.task.correction.<key>` is the
  // per-key exception: a missing key falls through to input, and an explicit
  // null does not.
  correction: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  // Requested anchor-entity fields keyed by dotted path (`list` with
  // `anchorFields` only). Null/absent when the task is unanchored, the caller
  // may not read the anchor's entity type, or no field was requested.
  anchor: z.record(z.string(), z.unknown()).nullable().optional(),
  // Populated by list/get to indicate the calling user watches this task.
  // Optional so existing handlers that don't set it remain valid.
  isWatching: z.boolean().optional(),
  // Ids of this task's direct subtasks. Populated by `get`; optional so
  // list/batch handlers don't pay the extra query.
  subtaskIds: z.array(z.uuid()).optional(),
});

// --- Input schemas ---

export const CreateTaskInputSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().max(10000).optional(),
  priority: TaskPrioritySchema.optional(),
  assigneeType: TaskAssigneeTypeSchema.optional(),
  assigneeUserId: z.uuid().optional(),
  assigneeAgentDefinitionId: z.uuid().optional(),
  dueDate: z.coerce.date().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  tags: z.array(z.string()).optional(),
  // Omit to place the task in the organization's default namespace.
  taskNamespaceId: z.uuid().optional(),
  // Give the task a type from the org's registry (validated server-side).
  taskTypeId: z.uuid().optional(),
  // Create the task as a subtask of an existing same-org task.
  parentTaskId: z.uuid().optional(),
  // Anchor the (typed) task to a business entity. Requires taskTypeId; the
  // type must declare a matching anchorEntityType.
  anchorEntityType: z.enum(SystemEntity).optional(),
  anchorEntityId: z.uuid().optional(),
  // Typed payload required when the selected task type declares an input
  // schema. Validated server-side against that schema's fields.
  input: z.record(z.string(), z.unknown()).optional(),
});

export const UpdateTaskInputSchema = z.object({
  id: z.uuid(),
  title: z.string().min(1).max(500).optional(),
  description: z.string().max(10000).nullable().optional(),
  priority: TaskPrioritySchema.optional(),
  dueDate: z.coerce.date().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  tags: z.array(z.string()).nullable().optional(),
  // Move the task to another namespace. The task is re-numbered from the
  // destination's sequence; its old number is abandoned (a permanent gap).
  taskNamespaceId: z.uuid().optional(),
  // Re-parent the task (null detaches it). Cycles are rejected server-side.
  parentTaskId: z.uuid().nullable().optional(),
  // Replace this task's playbook overrides (null clears them). Entries merge
  // over the type's bindings by sopId; `excluded` removes one for this task.
  procedures: TaskProcedureBindingsSchema.nullable().optional(),
});

/**
 * Replace the task's correction. Deliberately not a field of `tasks.update`:
 * a title edit must not skip schema validation or optimistic concurrency.
 */
export const UpdateTaskCorrectionInputSchema = z.object({
  id: z.uuid(),
  expectedUpdatedAt: z.coerce.date(),
  correction: z.record(z.string(), z.unknown()),
});

// --- Playbook (resolved task procedures) ---

/**
 * One SOP on the task's resolved playbook: the effective binding plus the SOP
 * summary and, for `required` + `run`, whether a successful run is recorded
 * against this task.
 */
export const TaskPlaybookEntrySchema = z.object({
  sopId: z.uuid(),
  mode: z.enum(["required", "suggested"]),
  phase: z.enum(TASK_PROCEDURE_PHASES),
  whenToUse: z.string().nullable(),
  pinnedVersionId: z.uuid().nullable(),
  parameterBindings: z.record(z.string(), z.string()),
  // Where the effective binding came from.
  source: z.enum(["task_type", "task"]),
  // Bindings whose SOP no longer resolves are reported in `missing` instead.
  sop: z.object({
    id: z.uuid(),
    name: z.string(),
    title: z.string(),
    version: z.number().int(),
    status: z.string(),
  }),
  // Engagement evidence for this task: a recorded successful replay, or a
  // get_procedure / run_procedure call. Always true for suggested entries.
  satisfied: z.boolean(),
  lastRun: z
    .object({
      status: z.string(),
      finishedAt: z.coerce.date().nullable(),
    })
    .nullable(),
});

export const ProcedureReadinessIssueSchema = z.object({
  sopId: z.uuid(),
  title: z.string(),
  code: z.enum(["not_allowed", "unavailable", "version_unavailable"]),
});

/**
 * A procedure version the task locked on first activation. Every later
 * attempt reuses `lockedVersion` until a human releases it.
 */
export const TaskProcedureLockSchema = z.object({
  sopId: z.string(),
  // Procedure name captured when the lock was taken.
  name: z.string(),
  lockedVersion: z.number().int().positive(),
  // The version a fresh activation would pin now (the binding's pinned
  // version, else the published one); null when it cannot resolve.
  latestVersion: z.number().int().nullable(),
});

export const TaskPlaybookResponseSchema = z.object({
  taskId: z.uuid(),
  // Whether completion is gated on the required entries: true once an agent
  // has worked the task. A task nobody automated is never blocked.
  gateApplies: z.boolean(),
  readinessIssues: z.array(ProcedureReadinessIssueSchema).optional(),
  required: z.array(TaskPlaybookEntrySchema),
  suggested: z.array(TaskPlaybookEntrySchema),
  // Bound to the task but outside the assigned agent's procedure allowlist —
  // a configuration warning, not an error.
  outsideAllowlist: z.array(TaskPlaybookEntrySchema),
  // Type-level bindings switched off by a task override.
  excluded: z.array(z.uuid()),
  // Bound sopIds that no longer resolve.
  missing: z.array(z.uuid()),
  // Rest of the assigned agent's allowlist (the "library" shelf).
  library: z.array(z.uuid()),
  // Procedure versions this task locked on first activation
  // (core_tasks.procedure_session_context). Optional so older serializers
  // stay valid.
  procedureLocks: z.array(TaskProcedureLockSchema).optional(),
});

export const ReleaseTaskProcedureLockInputSchema = z.object({
  taskId: z.uuid(),
  sopId: z.string().min(1),
});

export const ReleaseTaskProcedureLockResponseSchema = z.object({
  sopId: z.string(),
  releasedVersion: z.number().int().positive(),
});

/**
 * Give an UNTYPED task a type from the org's registry (one-way: typed tasks
 * cannot be re-typed). The anchor entity *type* is never supplied — it is the
 * type's declared `anchorEntityType`; the caller only provides the entity id,
 * which is required exactly when the type declares an anchor.
 */
export const ConvertTaskToTypeInputSchema = z.object({
  id: z.uuid(),
  taskTypeId: z.uuid(),
  anchorEntityId: z.uuid().optional(),
  // Optionally attach the task to an existing same-org parent at the same
  // time (cycles are rejected server-side). Omit to leave parentage as-is.
  parentTaskId: z.uuid().optional(),
  // Typed payload required when the selected task type declares an input
  // schema. Validated server-side against that schema's fields.
  input: z.record(z.string(), z.unknown()).optional(),
});

export const TaskAnchorReferenceFilterSchema = z.object({
  // A catalog path whose entry has `references` (e.g. `supplierId`).
  path: z.string().trim().min(1).max(200),
  ids: z.array(z.uuid()).min(1).max(100),
});

export const ListTasksInputSchema = z.object({
  limit: z.int().min(1).max(100).prefault(20),
  offset: z.int().min(0).prefault(0),
  // Filters
  status: TaskStatusSchema.optional(),
  statuses: z.array(TaskStatusSchema).optional(),
  priority: TaskPrioritySchema.optional(),
  assigneeType: TaskAssigneeTypeSchema.optional(),
  assigneeUserId: z.uuid().optional(),
  assigneeAgentDefinitionId: z.uuid().optional(),
  // Tasks with neither a user nor an agent assignee. Cannot be combined with
  // the assignee filters above.
  unassigned: z.boolean().optional(),
  // With assigneeAgentDefinitionId: also match tasks escalated away from that
  // agent (a needs_input hand-off reassigns the task to a user, keeping the
  // original agent only in metadata.escalatedFromAgentDefinitionId).
  includeEscalatedFromAgent: z.boolean().optional(),
  createdByUserId: z.uuid().optional(),
  watchedByUserId: z.uuid().optional(),
  taskNamespaceId: z.uuid().optional(),
  taskTypeId: z.uuid().optional(),
  parentTaskId: z.uuid().optional(),
  anchorEntityType: z.enum(SystemEntity).optional(),
  anchorEntityId: z.uuid().optional(),
  // Terminal outcome match (queue-page facet). Only terminal tasks carry an
  // outcome, so filtering implicitly narrows to concluded tasks.
  outcome: z.string().min(1).optional(),
  // Exact reason on a concluded task. Applied with the other filters, before
  // the page limit, so a match is not dropped by unrelated terminal tasks.
  outcomeReason: z.string().min(1).optional(),
  dueBefore: z.coerce.date().optional(),
  dueAfter: z.coerce.date().optional(),
  // Activity-axis date ranges. Unlike dueDate (often null), createdAt/updatedAt
  // are always set, so a range on them is the useful default for an inbox.
  createdAfter: z.coerce.date().optional(),
  createdBefore: z.coerce.date().optional(),
  updatedAfter: z.coerce.date().optional(),
  updatedBefore: z.coerce.date().optional(),
  search: z.string().optional(),
  // Human queues set this to hide tasks whose snooze is still in the future.
  // Other callers leave it unset and still receive those tasks.
  excludeSnoozed: z
    .boolean()
    .optional()
    .describe(
      "When true, hide tasks whose snooze is still in the future. Omit to include snoozed tasks.",
    ),
  // Sort
  sortBy: z
    .enum(["createdAt", "updatedAt", "dueDate", "priority", "status", "title"])
    .optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  // Anchor-entity field paths (e.g. `invoiceNumber`, `extractedData.total`)
  // to return on each row as `anchor`. Blocked or unknown paths are dropped;
  // anchor columns are not sortable.
  anchorFields: z
    .array(z.string().trim().min(1).max(200))
    .max(MAX_TASK_ANCHOR_FIELDS)
    .optional()
    .describe(
      "Field paths on each task's anchor entity (e.g. `invoiceNumber`, `extractedData.total`) to return on the row as `anchor`. Blocked or unknown paths are dropped.",
    ),
  // Keep tasks whose anchor's reference column holds one of the ids (e.g. AP
  // invoice tasks for given suppliers). Requires `anchorEntityType`; each path
  // must be a reference field in that type's anchor field catalog.
  anchorReferenceFilters: z
    .array(TaskAnchorReferenceFilterSchema)
    .max(5)
    .optional()
    .describe(
      "Keep tasks whose anchor record's reference field (e.g. `supplierId` on an AP invoice) holds one of the given ids. Requires `anchorEntityType`.",
    ),
});

export const ListTasksResponseSchema = z.object({
  tasks: z.array(TaskResponseSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
    // Set when excludeSnoozed hid rows: how many matching tasks are still snoozed.
    snoozedCount: z.number().int().nonnegative().optional(),
  }),
});

export const TaskAnchorFieldCatalogEntrySchema = z.object({
  // Top-level column of the anchor entity's model; JSON columns accept
  // deeper dotted paths in `anchorFields`.
  path: z.string(),
  type: z.enum(["string", "number", "decimal", "date", "boolean", "json"]),
  // Set when the column is a foreign key to another record (e.g. an AP
  // invoice's `supplierId` → AP_SUPPLIER): the value is that record's id, and
  // `anchorReferenceFilters` can filter the list on it.
  references: z.enum(SystemEntity).optional(),
});

export const TaskAnchorFieldCatalogInputSchema = z.object({
  entityType: z.enum(SystemEntity),
});

export const TaskAnchorFieldCatalogResponseSchema = z.object({
  // False when the entity type has no org-scoped model or the caller may not
  // read it; `fields` is then empty.
  supported: z.boolean(),
  fields: z.array(TaskAnchorFieldCatalogEntrySchema),
});

export const SnoozeTasksInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(100),
  until: z.coerce.date(),
  preset: z.enum(TASK_SNOOZE_PRESETS),
  note: z.string().trim().max(2000).optional(),
  clearsOnEdit: z.boolean().prefault(true),
  confirmSubtasks: z.boolean().prefault(false),
  confirmPastDue: z.boolean().prefault(false),
});

export const SnoozeTasksResponseSchema = z.object({
  tasks: z.array(TaskResponseSchema),
  skipped: z.array(
    z.object({
      taskId: z.uuid(),
      reason: z.enum(["not_found", "not_snoozable"]),
    }),
  ),
});

export const GetAgentRetrySnoozeInputSchema = z.object({
  taskId: z.uuid(),
});

// The agent retry behind a task's active snooze; null when a person snoozed
// it or it is not snoozed.
export const AgentRetrySnoozeSchema = z
  .object({
    agentDefinitionId: z.uuid(),
    agentName: z.string().nullable(),
    retryAt: z.coerce.date(),
    reason: z.string().nullable(),
    attempt: z.number().int().positive(),
    maxAttempts: z.number().int().positive(),
  })
  .nullable();

export const ClearTaskSnoozesInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(100),
});

/** Distinct assignees matching type / status / outcome. */
export const ListAssigneeFacetsInputSchema = z.object({
  taskTypeId: z.uuid().optional(),
  statuses: z.array(TaskStatusSchema).optional(),
  outcome: z.string().min(1).optional(),
  excludeSnoozed: z.boolean().optional(),
});

export const AssigneeFacetActorSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});

export const ListAssigneeFacetsResponseSchema = z.object({
  users: z.array(AssigneeFacetActorSchema),
  agents: z.array(AssigneeFacetActorSchema),
});

export const AgentOpenTaskCountsInputSchema = z.object({
  agentDefinitionId: z.uuid(),
});

export const AgentOpenTaskCountsResponseSchema = z.object({
  // Open tasks currently assigned to the agent, by status.
  running: z.int().nonnegative(),
  queued: z.int().nonnegative(),
  needsInput: z.int().nonnegative(),
  // needs_input tasks the agent escalated, now assigned to someone else.
  handedOff: z.int().nonnegative(),
});

export const AssignTaskInputSchema = z.object({
  id: z.uuid(),
  assigneeType: TaskAssigneeTypeSchema,
  assigneeUserId: z.uuid().optional(),
  assigneeAgentDefinitionId: z.uuid().optional(),
});

export const TransitionTaskInputSchema = z.object({
  id: z.uuid(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

// Terminal transitions (complete/fail/cancel) additionally accept an outcome.
// Validated server-side against the task type's outcome vocabulary (or
// DEFAULT_TASK_OUTCOMES for untyped tasks); required for typed tasks whose
// type declares an outcome vocabulary.
export const TerminalTransitionTaskInputSchema =
  TransitionTaskInputSchema.extend({
    outcome: z.string().min(1).optional(),
    outcomeReason: z.string().max(10000).optional(),
    outcomeData: TaskOutcomeDataSchema.nullable().optional(),
    outcomeContractFingerprint: z.string().optional(),
    // Complete despite unmet `required` playbook procedures. Recorded as a
    // `playbook_waived` task action with the reason; without it the server
    // answers PRECONDITION_FAILED with `data.code = "PLAYBOOK_UNSATISFIED"`.
    waivePlaybook: z
      .object({ reason: z.string().trim().min(1).max(2000) })
      .optional(),
  });

export const BatchGetTasksInputSchema = z.object({
  assigneeAgentDefinitionId: z.uuid(),
  statuses: z.array(TaskStatusSchema).optional(),
  limit: z.int().min(1).max(100).prefault(20),
});

export const BatchUpdateTasksInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(100),
  status: TaskStatusSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const BatchReturnToAgentInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(100),
  /**
   * Return every task to this agent instead of the one that last worked it.
   * Omit to resolve the agent per task (current, else escalated-from).
   */
  agentDefinitionId: z.uuid().optional(),
});

/**
 * Hand many tasks to one assignee, or clear the assignee (`assigneeType: null`).
 * Closed tasks, tasks that already have this assignee, and ids the caller
 * cannot see are skipped. Status is unchanged.
 */
/** Most tasks one batch-assign call takes; callers split bigger selections. */
export const BATCH_ASSIGN_TASKS_MAX = 100;

export const BatchAssignTasksInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(BATCH_ASSIGN_TASKS_MAX),
  assigneeType: TaskAssigneeTypeSchema.nullable(),
  assigneeUserId: z.uuid().optional(),
  assigneeAgentDefinitionId: z.uuid().optional(),
});

export const BatchAssignTasksResponseSchema = z.object({
  assigned: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});

/**
 * A meaningful agent action anchored to the recording timeline. Drives the
 * task video player's timeline markers, click overlay, and skip-inactivity.
 * Screenshots are intentionally excluded — the task surface only needs markers.
 */
export const TaskRecordingActionSchema = z.object({
  id: z.uuid(),
  startedAt: z.iso.datetime(),
  actionType: z.string().nullable(),
  actionText: z.string().nullable(),
  coordinate: z.tuple([z.number(), z.number()]).nullable(),
  viewportOffset: z.tuple([z.number(), z.number()]).nullable(),
});

export const TaskRecordingResponseSchema = z.object({
  recordingDocumentId: z.string(),
  signedUrl: z.string(),
  agentRunCompletedAt: z.coerce.date().nullable(),
  actions: z.array(TaskRecordingActionSchema),
});

// --- Timeline (phase 3, transaction spine) ---

// Who did the thing. Compact by design: the phase-4 UI resolves display
// names; the timeline carries stable ids only. The workflow actor is the
// exception — the run's workflow name rides along because the UI has no
// batch name-resolution path for workflows (unlike users/agents).
export const TaskTimelineActorSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("user"), userId: z.uuid() }),
  z.object({
    type: z.literal("agent"),
    agentRunId: z.uuid().nullable(),
    agentDefinitionId: z.uuid().nullable(),
  }),
  z.object({
    type: z.literal("workflow"),
    workflowRunId: z.uuid(),
    // Lineage workflow id + name (hydrated server-side). Nullable: a deleted
    // run leaves the audit stamp behind. The pair lets the UI render
    // "Workflow <name>" linking to /workflows/$workflowId/runs/$workflowRunId.
    workflowId: z.uuid().nullable(),
    workflowName: z.string().nullable(),
  }),
  z.object({ type: z.literal("system") }),
]);

// Fields every timeline entry carries. `id` is unique across the union
// (`<kind>:<source row id>`), so the UI can key rows without knowing kinds.
const taskTimelineEntryBase = {
  id: z.string(),
  taskId: z.uuid(),
  timestamp: z.coerce.date(),
  actor: TaskTimelineActorSchema,
};

/**
 * One entry of the unified task audit timeline — a QUERY across real records
 * (task rows, audit_change_log, core_task_actions, wfv2_step_runs,
 * ai_agent_runs, ai_reasoning_audit, ai_operator_step), never a log the task
 * maintains itself. Discriminated on `kind`; each kind carries a compact
 * summary payload the phase-4 UI can render directly.
 */
export const TaskTimelineEntrySchema = z.discriminatedUnion("kind", [
  // The task (or one of its subtasks) came into existence.
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("task_created"),
    title: z.string(),
    parentTaskId: z.uuid().nullable(),
    taskTypeId: z.uuid().nullable(),
    anchorEntityType: z.string().nullable(),
    anchorEntityId: z.uuid().nullable(),
  }),
  // A status transition, read from audit_change_log (CRUD diff trigger).
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("task_status_changed"),
    previousStatus: z.string(),
    status: z.string(),
    outcome: z.string().nullable(),
    outcomeReason: z.string().nullable().optional(),
    outcomeData: TaskOutcomeDataSchema.nullable().optional(),
    outcomeContractSnapshot: TaskOutcomeContractSchema.nullable().optional(),
    sourceType: z.string().nullable(),
    sourceDetail: z.string().nullable(),
  }),
  // An assignee change, read from audit_change_log (CRUD diff trigger).
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("task_assignee_changed"),
    previousAssigneeType: z.string().nullable(),
    assigneeType: z.string().nullable(),
    previousAssigneeUserId: z.uuid().nullable(),
    assigneeUserId: z.uuid().nullable(),
    previousAssigneeAgentDefinitionId: z.uuid().nullable(),
    assigneeAgentDefinitionId: z.uuid().nullable(),
  }),
  // The task was soft-deleted, read from audit_change_log (the UPDATE that set
  // deleted_at). The actor is whoever deleted it.
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("task_deleted"),
  }),
  // Terminal conclusion derived from the task row itself — emitted only for
  // tasks whose terminal transition predates the core_tasks audit trigger
  // (otherwise the audit-sourced task_status_changed entry covers it).
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("task_concluded"),
    status: z.string(),
    outcome: z.string().nullable(),
    outcomeReason: z.string().nullable(),
    outcomeData: TaskOutcomeDataSchema.nullable().optional(),
    outcomeContractSnapshot: TaskOutcomeContractSchema.nullable().optional(),
  }),
  // An attributable human action recorded via TaskActionService.
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("human_action"),
    actionKey: z.string(),
    payload: z.record(z.string(), z.unknown()).nullable(),
  }),
  // A workflow step run stamped with this task (wfv2_step_runs.task_id).
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("workflow_step_run"),
    stepRunId: z.uuid(),
    workflowRunId: z.uuid().nullable(),
    workflowStepId: z.uuid().nullable(),
    stepName: z.string().nullable(),
    stepType: z.string().nullable(),
    toolType: z.string().nullable(),
    status: z.string(),
    startedAt: z.coerce.date().nullable(),
    completedAt: z.coerce.date().nullable(),
  }),
  // An agent run that worked this task (ai_agent_runs.task_id).
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("agent_run"),
    agentRunId: z.uuid(),
    agentDefinitionId: z.uuid().nullable(),
    status: z.string(),
    error: z.string().nullable(),
    startedAt: z.coerce.date().nullable(),
    completedAt: z.coerce.date().nullable(),
    runSummary: z
      .object({
        agentName: z.string().nullable(),
        outcome: z.string().nullable(),
        summary: z.string().nullable(),
        question: z.string().nullable(),
        sourceStepRunId: z.uuid().nullable(),
        stepCount: z.number().int().nonnegative(),
        errorCount: z.number().int().nonnegative(),
      })
      .optional(),
  }),
  // An AI decision record (ai_reasoning_audit.task_id).
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("ai_reasoning"),
    action: z.string(),
    subjectType: z.string(),
    subjectId: z.string(),
    confidence: z.string(),
    reasoning: z.string().nullable(),
    resultType: z.string().nullable(),
    resultId: z.string().nullable(),
    source: z.string(),
    createdBy: z.string(),
  }),
  // One row from core_task_snoozes: a snooze, or a clear and why it cleared.
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("task_snooze"),
    reason: z.enum(TASK_SNOOZE_REASONS),
    snoozedUntil: z.coerce.date().nullable(),
    previousSnoozedUntil: z.coerce.date().nullable(),
    preset: z.enum(TASK_SNOOZE_PRESETS).nullable(),
    note: z.string().nullable(),
    clearsOnEdit: z.boolean().nullable(),
  }),
  // A canonical operator step of an agent run working this task
  // (ai_operator_step.task_id — the core task, not the A2A protocol id).
  z.object({
    ...taskTimelineEntryBase,
    kind: z.literal("operator_step"),
    operatorStepId: z.uuid(),
    agentRunId: z.uuid(),
    stepIndex: z.number().int(),
    status: z.string(),
    isTerminal: z.boolean(),
    a2aTaskId: z.string().nullable(),
    agentName: z.string().nullable().optional(),
    actionTools: z.array(z.string()).optional(),
  }),
]);

export const GetTaskTimelineInputSchema = z.object({
  id: z.uuid(),
  view: z.enum(["summary", "events"]).prefault("events"),
  limit: z.int().min(1).max(200).prefault(50),
  offset: z.int().min(0).prefault(0),
});

// --- Execute a declared task action (phase 4, transaction spine) ---

export const ExecuteTaskActionInputSchema = z.object({
  /** Task id the action is invoked on. */
  id: z.uuid(),
  /** Key of an action declared in the task type's config. */
  actionKey: z.string().min(1),
  /**
   * Submitted form values. Validated server-side against the action's
   * declared formSchema (strict — undeclared keys are rejected).
   */
  payload: z.record(z.string(), z.unknown()).optional(),
});

/** What the action's declared effect actually did. */
export const ExecuteTaskActionEffectResultSchema = z.discriminatedUnion(
  "kind",
  [
    z.object({
      kind: z.literal("emit_event"),
      eventKey: z.string(),
    }),
    z.object({
      kind: z.literal("start_workflow"),
      workflowTemplateKey: z.string(),
      workflowRunId: z.uuid(),
    }),
    z.object({
      kind: z.literal("set_outcome"),
      status: z.enum(TERMINAL_TASK_STATUSES),
      outcome: z.string().nullable(),
    }),
  ],
);

export const ExecuteTaskActionResponseSchema = z.object({
  success: z.boolean(),
  /** Id of the recorded core_task_actions row (the human-action audit fact). */
  taskActionId: z.uuid(),
  effect: ExecuteTaskActionEffectResultSchema,
});

// --- Derived automation score (autonomy + intervention rates) ---

export const TaskAutomationIntendedAssigneeSchema = z.enum(["agent", "human"]);

export const TaskAutomationCloserKindSchema = z.enum([
  "user",
  "agent",
  "workflow",
  "system",
  "none",
]);

export const TaskAutomationItemSchema = z.object({
  typeKey: z.string(),
  intendedAssignee: TaskAutomationIntendedAssigneeSchema.nullable(),
  closedByKind: TaskAutomationCloserKindSchema,
  passedNeedsInput: z.boolean(),
  reassignedAgentToUser: z.boolean(),
  closedAutonomously: z.boolean(),
  intervened: z.boolean(),
});

// --- AI cost ---

/**
 * How much of a run's paid model usage was captured and priced.
 *
 * - `complete`: every proxy call it made was recorded and priced.
 * - `partial`: some recorded calls had no price from the proxy.
 * - `not_captured`: the run used a model but no call was recorded — it predates
 *   capture, or ran without the proxy. Unknown, not zero.
 * - `no_ai_usage`: the run made no model calls (yet).
 */
export const TaskAiCostRunCoverageSchema = z.enum([
  "complete",
  "partial",
  "not_captured",
  "no_ai_usage",
]);

/** Task-level coverage; `unavailable` means no attributed call was priced at all. */
export const TaskAiCostCoverageSchema = z.enum([
  "complete",
  "partial",
  "unavailable",
  "no_ai_usage",
]);

export const TaskAiCostRunSchema = z.object({
  runId: z.uuid(),
  parentRunId: z.uuid().nullable(),
  /** The run's own task stamp; null for an untasked child of a task's run. */
  taskId: z.uuid().nullable(),
  agentDefinitionId: z.uuid().nullable(),
  agentDefinitionVersionId: z.uuid().nullable(),
  agentName: z.string().nullable(),
  workflowRunId: z.uuid().nullable(),
  status: z.enum(["running", "completed", "failed", "blocked"]),
  triggerKind: z.string().nullable(),
  startedAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
  llmSteps: z.number().int().nullable(),
  inputTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  /** Proxy calls recorded under this run (its children record their own). */
  callCount: z.number().int(),
  pricedCallCount: z.number().int(),
  /** Decimal string in USD; null when none of its calls were priced. */
  costUsd: z.string().nullable(),
  coverage: TaskAiCostRunCoverageSchema,
  /** The run drove ai-service operator work, whose model usage is not priced here. */
  excludesAiServiceUsage: z.boolean(),
  firstCallAt: z.iso.datetime().nullable(),
  lastCallAt: z.iso.datetime().nullable(),
});

export const TaskAiCostSchema = z.object({
  taskId: z.uuid(),
  /** Sum of the runs' priced calls, decimal string; null when nothing was priced. */
  totalCostUsd: z.string().nullable(),
  currency: z.literal("USD"),
  /** The LiteLLM proxy's per-call price-list estimate — not a provider invoice. */
  source: z.literal("proxy_estimate"),
  coverage: TaskAiCostCoverageSchema,
  runCount: z.number().int(),
  callCount: z.number().int(),
  pricedCallCount: z.number().int(),
  notCapturedRunCount: z.number().int(),
  aiServiceRunCount: z.number().int(),
  /** First and last recorded call — the period the total covers. */
  periodStart: z.iso.datetime().nullable(),
  periodEnd: z.iso.datetime().nullable(),
  /** Every run attributed to the task: its runs, retries and their child runs. */
  runs: z.array(TaskAiCostRunSchema),
});

export type TaskAiCostRunCoverage = z.infer<typeof TaskAiCostRunCoverageSchema>;
export type TaskAiCostCoverage = z.infer<typeof TaskAiCostCoverageSchema>;
export type TaskAiCostRun = z.infer<typeof TaskAiCostRunSchema>;
export type TaskAiCost = z.infer<typeof TaskAiCostSchema>;

export const GetTaskAutomationResponseSchema = z.object({
  taskId: z.uuid(),
  autonomyRate: z.number().nullable(),
  interventionRate: z.number().nullable(),
  items: z.array(TaskAutomationItemSchema),
  isFullyAutonomous: z.boolean(),
});

export const TaskAutomationStatsInputSchema = z.object({
  taskTypeKey: z.string().min(1),
  since: z.coerce.date().optional(),
  until: z.coerce.date().optional(),
});

export const TaskAutomationStatsResponseSchema = z.object({
  taskTypeKey: z.string(),
  taskCount: z.number().int(),
  truncated: z.boolean(),
  fullyAutonomousCount: z.number().int(),
  meanAutonomyRate: z.number().nullable(),
  meanInterventionRate: z.number().nullable(),
});

// --- Agent help requests + human provide-input (phase 6) ---

/**
 * The task's open help request — the structured question an agent recorded
 * when it escalated the task to `needs_input`. Null (from getOpenHelpRequest)
 * when the task is not waiting for input or was escalated before help
 * requests were recorded.
 */
export const TaskHelpRequestSchema = z.object({
  /** Id of the `agent_help_requested` core_task_actions row. */
  taskActionId: z.uuid(),
  taskId: z.uuid(),
  question: z.string(),
  context: z.string().nullable(),
  attempted: z.string().nullable(),
  agentDefinitionId: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export const ProvideTaskInputInputSchema = z.object({
  /** Task id (must be in `needs_input`). */
  id: z.uuid(),
  /** The human's free-text answer to the agent's open question. */
  response: z.string().min(1).max(10000),
  /**
   * The help request being answered. Omit to answer the task's latest open
   * request (server-resolved).
   */
  helpRequestActionId: z.uuid().optional(),
});

export const ProvideTaskInputResponseSchema = z.object({
  /** The task after the `needs_input` → `pending` transition. */
  task: TaskResponseSchema,
  /** Id of the recorded `human_input_provided` core_task_actions row. */
  taskActionId: z.uuid(),
  /**
   * Who picks the answer up: `agent` re-queues the task for the background
   * agent that escalated it; `nami` hands it to Nami, who investigates in the
   * background and follows up with the caller through the task chat.
   */
  followUp: z.enum(["agent", "nami"]),
});

/**
 * The caller's Nami conversation bound to a task (`tasks.getAgentThread`).
 */
export const TaskAgentThreadSchema = z.object({
  /**
   * The caller's chat thread with Nami on this task. Null when Nami has not
   * worked this task for the caller yet — the task page then starts a fresh
   * conversation in the `tasks` chat module.
   */
  threadId: z.uuid().nullable(),
  /**
   * Nami's agent-definition id in the caller's organization — the actor her
   * task actions, comments, and assignments are attributed to. Null until she
   * has acted on any task in the org. Lets the UI recognize an open help
   * request as Nami's (and offer to continue it in the chat).
   */
  agentDefinitionId: z.string().nullable(),
});

export const GetTaskTimelineResponseSchema = z.object({
  entries: z.array(TaskTimelineEntrySchema),
  // Every task in the collected subtree (root first), so the UI can label
  // entries by task without a second round trip.
  taskIds: z.array(z.uuid()),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

// --- Contracts ---

export const tasksContracts = {
  create: oc.input(CreateTaskInputSchema).output(TaskResponseSchema).route({
    method: "POST",
    summary: "Create a task",
    description:
      "Creates a task with title, priority, due date, assignee, optional type, anchor record, and parent. Unassigned typed tasks get the type's default assignee; emits a task-created event.",
  }),

  get: oc
    // UUID or derived display id (`AP-1234`). Keep minLength at 0 so this
    // stays additive vs the previous z.uuid() contract (oasdiff ERR).
    // Empty or invalid shapes return null from the handler.
    .input(z.object({ id: z.string().trim().max(80) }))
    .output(TaskResponseSchema.nullable())
    .route({
      method: "GET",
      summary: "Get a task by id",
      description:
        "Returns one task by UUID or display id (e.g. TASK-42), including status, assignee, type, anchor record, and outcome. Returns null when not found.",
    }),

  list: oc.input(ListTasksInputSchema).output(ListTasksResponseSchema).route({
    method: "GET",
    summary: "List tasks",
    description:
      "Searches and pages tasks filtered by status, priority, assignee, type, namespace, anchor record, outcome, due or created dates, and snooze. Can include anchor record fields per row.",
  }),

  anchorFieldCatalog: oc
    .input(TaskAnchorFieldCatalogInputSchema)
    .output(TaskAnchorFieldCatalogResponseSchema)
    .route({
      method: "GET",
      summary: "List anchor record fields for task columns",
      description:
        "Returns the selectable fields of a record type that tasks anchor to, with data types, for adding columns to a task list. Empty when unsupported or not readable.",
    }),

  snooze: oc
    .input(SnoozeTasksInputSchema)
    .output(SnoozeTasksResponseSchema)
    .route({
      method: "POST",
      summary: "Snooze tasks until a date",
      description:
        "Hides pending or needs-input tasks from human queues until a time, with an optional note. Visibility only; status and due date are unchanged. Returns snoozed and skipped tasks.",
    }),

  clearSnooze: oc
    .input(ClearTaskSnoozesInputSchema)
    .output(SnoozeTasksResponseSchema)
    .route({
      method: "POST",
      summary: "Unsnooze tasks",
      description:
        "Clears the snooze on the given tasks so they reappear in human queues immediately, recording the clear in snooze history. Returns updated and skipped tasks.",
    }),

  agentRetry: oc
    .input(GetAgentRetrySnoozeInputSchema)
    .output(AgentRetrySnoozeSchema)
    .route({
      method: "GET",
      summary: "Get a task's agent retry snooze",
      description:
        "When an agent snoozed the task to retry it later, returns the agent, its reason, the retry time, and which retry this is out of the limit before the task escalates to a person. Null otherwise.",
    }),

  listAssigneeFacets: oc
    .input(ListAssigneeFacetsInputSchema)
    .output(ListAssigneeFacetsResponseSchema)
    .route({
      method: "GET",
      summary: "List task assignee filter options",
      description:
        "Returns the distinct users and agents assigned to tasks matching a task type, statuses, outcome, and snooze filter, for an assignee filter.",
    }),

  agentOpenCounts: oc
    .input(AgentOpenTaskCountsInputSchema)
    .output(AgentOpenTaskCountsResponseSchema)
    .route({
      method: "GET",
      summary: "Count an agent's open tasks",
      description:
        "Counts the open tasks currently assigned to an agent by status (running, queued, needs input), plus the needs-input tasks it escalated that now sit with a person. Counts cover the tasks the caller can see.",
    }),

  update: oc.input(UpdateTaskInputSchema).output(TaskResponseSchema).route({
    method: "PUT",
    summary: "Update a task",
    description:
      "Edits a task's title, description, priority, due date, metadata, tags, namespace, parent, or playbook overrides. Type and anchor record cannot change here.",
  }),

  updateCorrection: oc
    .input(UpdateTaskCorrectionInputSchema)
    .output(TaskResponseSchema)
    .route({
      method: "PUT",
      summary: "Correct a task's typed input data",
      description:
        "Replaces the corrected input values on a typed task, validated against the type's input schema. Requires the task's last-updated time to guard against concurrent edits.",
    }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      method: "DELETE",
      summary: "Delete a task",
      description:
        "Soft-deletes a task. Refused for a task that belongs to a price-list update case, which must be deleted through the case.",
    }),

  // Assign a type (and, when the type declares one, an anchor) to an
  // untyped task. Distinct from `update`, which deliberately keeps type and
  // anchor immutable.
  convertToType: oc
    .input(ConvertTaskToTypeInputSchema)
    .output(TaskResponseSchema)
    .route({
      method: "POST",
      summary: "Convert an untyped task to a task type",
      description:
        "Gives an untyped task a type from the registry, plus its anchor record and typed input when the type requires them. One-way: typed tasks cannot be re-typed.",
    }),

  assign: oc.input(AssignTaskInputSchema).output(TaskResponseSchema).route({
    method: "PUT",
    summary: "Assign a task to a user or agent",
    description:
      "Sets a task's assignee to an organization user or agent. A user assignee starts watching the task and gets notified; any active snooze clears.",
  }),

  unassign: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskResponseSchema)
    .route({
      method: "PUT",
      summary: "Unassign a task",
      description:
        "Removes the user or agent assignee from a task, leaving it unassigned. Any active snooze clears.",
    }),

  start: oc.input(TransitionTaskInputSchema).output(TaskResponseSchema).route({
    method: "POST",
    summary: "Start working a task",
    description:
      "Moves a task to in progress and records the start time. Emits a task status-changed event.",
  }),

  complete: oc
    .input(TerminalTransitionTaskInputSchema)
    .output(TaskResponseSchema)
    .route({
      method: "POST",
      summary: "Complete a task with an outcome",
      description:
        "Marks a task completed with an optional outcome, reason, and outcome data. Typed tasks may require an outcome, finished subtasks, and required playbook procedures unless waived.",
    }),

  fail: oc
    .input(TerminalTransitionTaskInputSchema)
    .output(TaskResponseSchema)
    .route({
      method: "POST",
      summary: "Mark a task as failed",
      description:
        "Moves a task to failed with an optional outcome, reason, and outcome data validated against the task type. Emits a task status-changed event.",
    }),

  cancel: oc
    .input(TerminalTransitionTaskInputSchema)
    .output(TaskResponseSchema)
    .route({
      method: "POST",
      summary: "Cancel a task",
      description:
        "Moves a task to cancelled with an optional outcome, reason, and outcome data validated against the task type. Emits a task status-changed event.",
    }),

  batchGet: oc
    .input(BatchGetTasksInputSchema)
    .output(z.array(TaskResponseSchema))
    .route({
      method: "POST",
      summary: "List tasks assigned to an agent",
      description:
        "Returns up to 100 tasks assigned to one agent, optionally filtered by status.",
    }),

  batchUpdate: oc
    .input(BatchUpdateTasksInputSchema)
    .output(z.array(TaskResponseSchema))
    .route({
      method: "PUT",
      summary: "Bulk update task status or metadata",
      description:
        "Sets the status and/or metadata on up to 100 tasks in one transaction, releasing any agent claim when status changes. Emits status-changed events.",
    }),

  // Re-assign each task back to its originating agent and re-queue it (pending).
  // The target agent is resolved per-task on the server, so a single bulk call
  // can return many tasks each to its own agent.
  batchReturnToAgent: oc
    .input(BatchReturnToAgentInputSchema)
    .output(z.array(TaskResponseSchema))
    .route({
      method: "PUT",
      summary: "Return tasks to their agents",
      description:
        "Reassigns each task to its originating agent (current or escalated-from) and re-queues it as pending, so the agent works it again. Tasks without an agent are skipped. Pass agentDefinitionId to send every task to that active agent instead.",
    }),

  // Hand one task to the agent configured as its task type's default
  // assignee and re-queue it (pending) so that agent can claim it again.
  sendBackToAgent: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskResponseSchema)
    .route({
      method: "POST",
      summary: "Send a task back to its type agent",
      description:
        "Assigns one task to the agent configured as its task type's default assignee. Moves it to pending unless it is already pending, clearing a recorded outcome when reopening a closed task. Rejects when the type has no default agent, the agent is missing or inactive, or the task is already pending with that agent.",
    }),

  batchAssign: oc
    .input(BatchAssignTasksInputSchema)
    .output(BatchAssignTasksResponseSchema)
    .route({
      method: "PUT",
      summary: "Bulk assign tasks",
      description:
        "Assigns up to 100 tasks to one user or agent, or clears the assignee. Closed, already-assigned, and inaccessible tasks are skipped; status is unchanged. Returns assigned and skipped counts.",
    }),

  getRecording: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskRecordingResponseSchema.nullable())
    .route({
      method: "GET",
      summary: "Get a task's agent screen recording",
      description:
        "Returns a signed video URL for the latest agent run on a task plus timeline markers for each agent action. Null when there is no recording.",
    }),

  // The unified audit timeline for a task and its subtask subtree.
  getTimeline: oc
    .input(GetTaskTimelineInputSchema)
    .output(GetTaskTimelineResponseSchema)
    .route({
      method: "GET",
      summary: "Get a task's activity timeline",
      description:
        "Returns the paged audit history for a task and its subtasks: status and field changes, human and agent actions, workflow steps, and agent runs, as summary or full events.",
    }),

  // Invoke an action declared on the task type: validates the payload
  // against the declared formSchema, records a human action, and performs
  // the declared effect (emit_event / start_workflow).
  executeAction: oc
    .input(ExecuteTaskActionInputSchema)
    .output(ExecuteTaskActionResponseSchema)
    .route({
      method: "POST",
      summary: "Run a task type action on a task",
      description:
        "Runs an action declared on the task's type with submitted form values, records it, and performs its effect: emit an event, start a workflow, or close the task.",
    }),

  getAiCost: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskAiCostSchema.nullable())
    .route({
      method: "GET",
      summary: "Get a task's AI cost",
      description:
        "Returns the estimated AI model spend attributed to a task, reconciled across its agent runs, retries and child runs, with per-run breakdown and coverage. Null when the task is not found.",
    }),

  getAutomation: oc
    .input(z.object({ id: z.uuid() }))
    .output(GetTaskAutomationResponseSchema)
    .route({
      method: "GET",
      summary: "Get a task's automation score",
      description:
        "Returns autonomy and intervention rates for a task and its subtasks: who closed each item, whether it needed human input, and whether it was fully autonomous.",
    }),

  automationStats: oc
    .input(TaskAutomationStatsInputSchema)
    .output(TaskAutomationStatsResponseSchema)
    .route({
      method: "GET",
      summary: "Get automation stats for a task type",
      description:
        "Returns task count, fully autonomous count, and mean autonomy and intervention rates for tasks of one type, optionally within a date range.",
    }),

  // The task's open help request (latest agent_help_requested action while
  // the task sits in needs_input), for the reply UI.
  getOpenHelpRequest: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskHelpRequestSchema.nullable())
    .route({
      method: "GET",
      summary: "Get an agent's open question on a task",
      description:
        "Returns the question, context, and attempted steps an agent recorded when it escalated a task for human input. Null when the task is not waiting for input.",
    }),

  // Answer an agent's open help request: records the human's response as a
  // task action, restores the escalating agent's assignment, and re-queues
  // the task (needs_input → pending) for the agent task loop.
  provideInput: oc
    .input(ProvideTaskInputInputSchema)
    .output(ProvideTaskInputResponseSchema)
    .route({
      method: "POST",
      summary: "Answer an agent's question on a task",
      description:
        "Records a human answer to the task's open help request and re-queues the task as pending, returning it to the escalating agent or to Nami for follow-up.",
    }),

  // The caller's Nami chat thread for this task, so the task page can resume
  // the conversation Nami started while working the task in the background.
  getAgentThread: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskAgentThreadSchema)
    .route({
      method: "GET",
      summary: "Get the assistant chat thread for a task",
      description:
        "Returns the caller's Nami conversation for a task, if Nami has worked it, plus Nami's agent id so the task page can resume the chat.",
    }),

  // The task's resolved playbook: type bindings merged with the task's own
  // overrides, hydrated with SOP summaries, partitioned against the assigned
  // agent's procedure allowlist, with per-entry gate status.
  getOutcomeContract: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskOutcomeContractResponseSchema)
    .route({
      method: "GET",
      summary: "Get a task's outcome requirements",
      description:
        "Returns the outcome options and fields required to close a task, whether they are pinned to the task, and any configuration error.",
    }),

  getPlaybook: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskPlaybookResponseSchema)
    .route({
      method: "GET",
      summary: "Get a task's playbook procedures",
      description:
        "Returns the task's resolved playbook: procedures from its type merged with task overrides, availability to the assigned agent, required status, and last run.",
    }),

  // Release the procedure version the task locked on first activation, so the
  // next attempt pins the version the procedure currently resolves to.
  // Refused while an agent run for the task is running.
  releaseProcedureLock: oc
    .input(ReleaseTaskProcedureLockInputSchema)
    .output(ReleaseTaskProcedureLockResponseSchema)
    .route({
      method: "POST",
      summary: "Release a task's locked procedure version",
      description:
        "Unpins the procedure version a task locked on first run so the next attempt uses the current version. Refused while an agent run for the task is running.",
    }),
} as const;

// --- Type exports ---

export type TaskPlaybookEntry = z.infer<typeof TaskPlaybookEntrySchema>;
export type TaskProcedureLock = z.infer<typeof TaskProcedureLockSchema>;
export type ReleaseTaskProcedureLockInput = z.infer<
  typeof ReleaseTaskProcedureLockInputSchema
>;
export type TaskPlaybookResponse = z.infer<typeof TaskPlaybookResponseSchema>;

export type TaskStatus = z.infer<typeof TaskStatusSchema>;
export type TaskPriority = z.infer<typeof TaskPrioritySchema>;
export type TaskAssigneeType = z.infer<typeof TaskAssigneeTypeSchema>;
export type TaskResponse = z.infer<typeof TaskResponseSchema>;
export type AgentRetrySnooze = z.infer<typeof AgentRetrySnoozeSchema>;
export type CreateTaskInput = z.infer<typeof CreateTaskInputSchema>;
export type UpdateTaskInput = z.infer<typeof UpdateTaskInputSchema>;
export type UpdateTaskCorrectionInput = z.infer<
  typeof UpdateTaskCorrectionInputSchema
>;
export type ConvertTaskToTypeInput = z.infer<
  typeof ConvertTaskToTypeInputSchema
>;
export type ListTasksInput = z.infer<typeof ListTasksInputSchema>;
export type ListTasksResponse = z.infer<typeof ListTasksResponseSchema>;
export type TaskAnchorFieldCatalogEntry = z.infer<
  typeof TaskAnchorFieldCatalogEntrySchema
>;
export type TaskAnchorReferenceFilter = z.infer<
  typeof TaskAnchorReferenceFilterSchema
>;
export type TaskAnchorFieldCatalogResponse = z.infer<
  typeof TaskAnchorFieldCatalogResponseSchema
>;
export type ListAssigneeFacetsInput = z.infer<
  typeof ListAssigneeFacetsInputSchema
>;
export type ListAssigneeFacetsResponse = z.infer<
  typeof ListAssigneeFacetsResponseSchema
>;
export type AgentOpenTaskCountsResponse = z.infer<
  typeof AgentOpenTaskCountsResponseSchema
>;
export type AssignTaskInput = z.infer<typeof AssignTaskInputSchema>;
export type TransitionTaskInput = z.infer<typeof TransitionTaskInputSchema>;
export type TerminalTransitionTaskInput = z.infer<
  typeof TerminalTransitionTaskInputSchema
>;
export type BatchGetTasksInput = z.infer<typeof BatchGetTasksInputSchema>;
export type BatchUpdateTasksInput = z.infer<typeof BatchUpdateTasksInputSchema>;
export type BatchReturnToAgentInput = z.infer<
  typeof BatchReturnToAgentInputSchema
>;
export type BatchAssignTasksInput = z.infer<typeof BatchAssignTasksInputSchema>;
export type BatchAssignTasksResponse = z.infer<
  typeof BatchAssignTasksResponseSchema
>;
export type TaskRecordingResponse = z.infer<typeof TaskRecordingResponseSchema>;
export type TaskRecordingAction = z.infer<typeof TaskRecordingActionSchema>;
export type ExecuteTaskActionInput = z.infer<
  typeof ExecuteTaskActionInputSchema
>;
export type ExecuteTaskActionEffectResult = z.infer<
  typeof ExecuteTaskActionEffectResultSchema
>;
export type ExecuteTaskActionResponse = z.infer<
  typeof ExecuteTaskActionResponseSchema
>;
export type TaskAutomationItemResponse = z.infer<
  typeof TaskAutomationItemSchema
>;
export type GetTaskAutomationResponse = z.infer<
  typeof GetTaskAutomationResponseSchema
>;
export type TaskAutomationStatsInput = z.infer<
  typeof TaskAutomationStatsInputSchema
>;
export type TaskAutomationStatsResponse = z.infer<
  typeof TaskAutomationStatsResponseSchema
>;
export type TaskHelpRequest = z.infer<typeof TaskHelpRequestSchema>;
export type ProvideTaskInputInput = z.infer<typeof ProvideTaskInputInputSchema>;
export type ProvideTaskInputResponse = z.infer<
  typeof ProvideTaskInputResponseSchema
>;
export type TaskAgentThread = z.infer<typeof TaskAgentThreadSchema>;
export type TaskTimelineActor = z.infer<typeof TaskTimelineActorSchema>;
export type TaskTimelineEntry = z.infer<typeof TaskTimelineEntrySchema>;
export type GetTaskTimelineInput = z.infer<typeof GetTaskTimelineInputSchema>;
export type GetTaskTimelineResponse = z.infer<
  typeof GetTaskTimelineResponseSchema
>;
